import { Heart } from "lucide-react";
import type { HrZone, HrZoneBuckets, HrZoneId } from "@/lib/heartRateZones";

interface HeartRateZoneChartProps {
  zones: HrZone[];
  buckets: HrZoneBuckets;
  maxHr?: number;
  maxHrSource?: 'measured' | 'estimated';
  avgBpm?: number | null;
  peakBpm?: number | null;
  /** Optional zone the user was aiming for in this workout. */
  targetZone?: HrZoneId;
  title?: string;
  className?: string;
}

const fmtDuration = (sec: number) => {
  if (!Number.isFinite(sec) || sec <= 0) return '0s';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  if (m === 0) return `${s}s`;
  if (s === 0) return `${m}m`;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
};

const zoneDescriptions: Record<string, string> = {
  z1: 'Very easy · recovery',
  z2: 'Easy · aerobic base',
  z3: 'Moderate · tempo',
  z4: 'Hard · threshold',
  z5: 'Max · VO₂',
};

/**
 * Horizontal bar chart that shows how long the user spent in each HR zone.
 * Designed to drop into the run summary or workout detail screens.
 */
export const HeartRateZoneChart = ({
  zones,
  buckets,
  maxHr,
  maxHrSource,
  avgBpm,
  peakBpm,
  targetZone,
  title = 'Heart rate zones',
  className,
}: HeartRateZoneChartProps) => {
  const maxPct = zones.reduce(
    (acc, z) => Math.max(acc, buckets.percentInZone[z.id] || 0),
    1,
  );

  // Find the zone the user spent the most time in.
  let dominantZone: HrZone | undefined;
  let dominantPct = 0;
  for (const z of zones) {
    const pct = buckets.percentInZone[z.id] || 0;
    if (pct > dominantPct) {
      dominantPct = pct;
      dominantZone = z;
    }
  }

  const targetZoneInfo = targetZone ? zones.find((z) => z.id === targetZone) : undefined;
  const targetPct = targetZone ? buckets.percentInZone[targetZone] || 0 : 0;

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Heart className="h-3.5 w-3.5 text-rose-500" />
          <label className="text-xs font-semibold">{title}</label>
        </div>
        {maxHr ? (
          <span className="text-[10px] text-muted-foreground">
            Max HR {maxHr} bpm{maxHrSource === 'estimated' ? ' (est.)' : ''}
          </span>
        ) : null}
      </div>

      {/* Headline strip: dominant zone + (optional) target zone progress. */}
      <div className="mb-2 grid grid-cols-2 gap-2">
        {dominantZone && (
          <div
            className="rounded-md p-2 border"
            style={{ borderColor: `${dominantZone.color}66`, background: `${dominantZone.color}14` }}
          >
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Most time in</div>
            <div className="text-sm font-bold" style={{ color: dominantZone.color }}>
              {dominantZone.id.toUpperCase()} · {dominantZone.label}
            </div>
            <div className="text-[10px] text-muted-foreground">
              {dominantPct.toFixed(0)}% · {fmtDuration(buckets.secondsInZone[dominantZone.id] || 0)}
            </div>
          </div>
        )}
        {targetZoneInfo && (
          <div
            className="rounded-md p-2 border"
            style={{ borderColor: `${targetZoneInfo.color}66`, background: `${targetZoneInfo.color}14` }}
          >
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Target zone</div>
            <div className="text-sm font-bold" style={{ color: targetZoneInfo.color }}>
              {targetZoneInfo.id.toUpperCase()} · {targetZoneInfo.label}
            </div>
            <div className="text-[10px] text-muted-foreground">
              {targetPct.toFixed(0)}% on target
            </div>
          </div>
        )}
      </div>

      <div className="space-y-1.5 bg-muted/20 rounded-md p-2">
        {zones.map((z) => {
          const pct = buckets.percentInZone[z.id] || 0;
          const sec = buckets.secondsInZone[z.id] || 0;
          const width = Math.max(2, (pct / maxPct) * 100);
          const isTarget = targetZone === z.id;
          return (
            <div key={z.id} className="flex items-center gap-2">
              <div className="w-20 shrink-0">
                <div
                  className="text-[10px] font-bold leading-tight"
                  style={{ color: z.color }}
                >
                  {z.id.toUpperCase()}{isTarget ? ' ●' : ''}
                </div>
                <div className="text-[9px] text-muted-foreground leading-tight">
                  {zoneDescriptions[z.id] || z.label}
                </div>
              </div>
              <div className="flex-1 min-w-0 relative h-4 rounded bg-muted/40 overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 rounded"
                  style={{ width: `${width}%`, backgroundColor: z.color, opacity: 0.85 }}
                />
                {z.lowerBpm && z.upperBpm ? (
                  <div className="absolute inset-0 flex items-center px-1.5 text-[9px] font-medium text-foreground/80">
                    {Math.round(z.lowerBpm)}–{Math.round(z.upperBpm)} bpm
                  </div>
                ) : null}
              </div>
              <div className="w-20 shrink-0 text-right text-[10px] text-muted-foreground tabular-nums">
                {pct.toFixed(1)}% · {fmtDuration(sec)}
              </div>
            </div>
          );
        })}
      </div>

      {(avgBpm || peakBpm) && (
        <div className="mt-1.5 flex justify-between text-[10px] text-muted-foreground">
          {avgBpm ? <span>Avg <span className="font-semibold text-foreground">{Math.round(avgBpm)}</span> bpm</span> : <span />}
          {peakBpm ? <span>Peak <span className="font-semibold text-foreground">{Math.round(peakBpm)}</span> bpm</span> : <span />}
        </div>
      )}
    </div>
  );
};

export default HeartRateZoneChart;
